import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { BasicDropDown, BasicDropDownController } from '@components-clean/core/basic-dropdown';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import { Icon } from '@components-clean/core/icons/icon';

interface BasicDropDownPageProps {
    isDisabled: boolean;
}

interface BasicDropDownOption {
    id: number | null;
    value: string;
    groupedBy?: string;
    disabled?: boolean;
}

const data: BasicDropDownOption[] = [
    'Pizza',
    'Burger',
    'Pasta',
    'Burrito',
    'Cupcake',
    'Cookies',
    'Eggs',
    'Bread',
    'Ham',
    'Dust',
].map((food, index) => ({
    id: index + 1,
    value: food,
    active: index !== 0,
}));

const groupedData: BasicDropDownOption[] = [
    {
        id: 1,
        value: 'Pizza',
        groupedBy: 'Dishes',
    },
    {
        id: 2,
        value: 'Burger',
        groupedBy: 'Dishes',
        disabled: true,
    },
    {
        id: 3,
        value: 'Pasta',
        groupedBy: 'Dishes',
    },
    {
        id: 4,
        value: 'Burrito',
        groupedBy: 'Dishes',
    },
    {
        id: 9,
        value: 'Eggs',
        groupedBy: 'Ingredients',
    },
    {
        id: 10,
        value: 'Ham',
        groupedBy: 'Ingredients',
        disabled: true,
    },
    {
        id: 11,
        value: 'Dust',
        groupedBy: 'Ingredients',
    },
    {
        id: 12,
        value: 'Milk',
        groupedBy: 'Ingredients',
    },
    {
        id: 5,
        value: 'Cupcake',
        groupedBy: 'Baked',
    },
    {
        id: 6,
        value: 'Cookies',
        groupedBy: 'Baked',
    },
    {
        id: 7,
        value: 'Bread',
        groupedBy: 'Baked',
    },
    {
        id: 8,
        value: 'Bun',
        groupedBy: 'Baked',
    },
];

type BasicDropDownForm = {
    dropdownSolid: BasicDropDownOption | null;
    dropdownSolidTooltip: BasicDropDownOption | null;
    dropdownSolidFilterable: BasicDropDownOption | null;
    dropdownSolidRequired: BasicDropDownOption | null;
    dropdownSolidPizzaInvalid: BasicDropDownOption | null;
    dropdownSolidReadonly: BasicDropDownOption | null;
    dropdownSolidInactive: BasicDropDownOption | null;
    dropdownGrouped: BasicDropDownOption | null;
    dropdownFlat: BasicDropDownOption | null;
    dropdownFlatFilterable: BasicDropDownOption | null;
    dropdownFlatRequired: BasicDropDownOption | null;
    dropdownFlatPizzaInvalid: BasicDropDownOption | null;
    dropdownFlatReadonly: BasicDropDownOption | null;
    dropdownFlatInactive: BasicDropDownOption | null;
    dropdownCardHeader: BasicDropDownOption | null;
    dropdownCustomSuffix: BasicDropDownOption | null;
    dropdownNoData: BasicDropDownOption | null;
};

const BasicDropDownPage = ({ isDisabled }: BasicDropDownPageProps) => {
    // React Hook Form
    const { control, handleSubmit } = useForm<BasicDropDownForm>({
        defaultValues: {
            dropdownSolid: data[0],
            dropdownSolidTooltip: null,
            dropdownSolidRequired: null,
            dropdownSolidPizzaInvalid: null,
            dropdownGrouped: null,
            dropdownFlat: data[0],
            dropdownFlatRequired: null,
            dropdownFlatPizzaInvalid: null,
            dropdownFlatReadonly: null,
            dropdownCardHeader: null,
            dropdownCustomSuffix: null,
            dropdownNoData: null,
        },
    });

    const onSubmit: SubmitHandler<BasicDropDownForm> = (dataHookForm) => {
        // eslint-disable-next-line no-alert
        alert(JSON.stringify(dataHookForm, null, 2));
    };

    const [readonly, setReadonly] = useState<boolean>(true);

    const [dropdownSolidReadOnly, setDropdownSolidReadOnly] = useState<BasicDropDownOption | null>(data[0]);
    const [dropdownFlatReadOnly, setDropdownFlatReadOnly] = useState<BasicDropDownOption | null>(data[0]);
    const [dropdownCardHeader, setDropdownCardHeader] = useState<BasicDropDownOption | null>(data[0]);

    return (
        <div className="k-gap-4" style={{ minWidth: 150, maxWidth: 350 }}>
            <form onSubmit={handleSubmit(onSubmit)} className="k-d-flex k-d-flex-col k-gap-4" noValidate>
                <h2 className="k-mb-0 !k-mt-0">Basic Dropdowns</h2>

                {/* Fill Mode = "Solid" (default) */}
                <BasicDropDownController
                    control={control}
                    name="dropdownSolid"
                    data={data}
                    data-testid="dropdownSolid"
                    label="Dropdown Solid - (Optional)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    filterable
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    name="dropdownSolidTooltip"
                    data={data}
                    data-testid="dropdownSolidTooltip"
                    label="Dropdown Solid with Tooltip - (Optional)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    filterable
                    tooltipText="Tooltip Text"
                />

                {/* NOTE: We might need this code later because currently, we need only 2 types of dropdowns */}

                <BasicDropDownController
                    control={control}
                    name="dropdownSolidRequired"
                    data={data}
                    data-testid="dropdownSolidRequired"
                    label="Dropdown Solid - (Required)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    required
                    filterable
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    name="dropdownSolidPizzaInvalid"
                    data={data}
                    data-testid="dropdownSolidPizzaInvalid"
                    label="Dropdown Solid - ('Pizza' is invalid)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    validateRules={{
                        validatePizza: (value) => (value?.value === 'Pizza' ? 'Pizza is out of stock' : true),
                    }}
                    filterable
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    name="dropdownGrouped"
                    data={groupedData}
                    data-testid="dropdownGrouped"
                    groupField="groupedBy"
                    label="Dropdown Grouped"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    filterable
                    allowClear
                />

                <BasicDropDown
                    data={[]}
                    data-testid="dropdownSolidDisabled"
                    label="Dropdown Solid - (Disabled)"
                    disabled
                    filterable
                    allowClear
                />

                <BasicDropDown
                    data={[]}
                    data-testid="dropdownSolidDisabledTooltip"
                    label="Dropdown Solid with Tooltip - (Disabled)"
                    disabled
                    filterable
                    tooltipText="Tooltip Text"
                />

                <BasicDropDown
                    data={data}
                    textField="value"
                    data-testid="dropdownReadonly"
                    label="Dropdown - (readonly)"
                    readOnly
                    filterable
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    name="dropdownCardHeader"
                    data={data}
                    value={dropdownCardHeader}
                    onChange={(e) => setDropdownCardHeader(e.value as BasicDropDownOption | null)}
                    textField="value"
                    data-testid="dropdownCardHeader"
                    label="Dropdown Solid - (Card Header)"
                    filterable
                    cardHeader
                    disabled={isDisabled}
                    dataItemKey="id"
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    name="dropdownSolidInactive"
                    data={data}
                    value={data[0]}
                    data-testid="dropdownSolidInactiveControlled"
                    label="Dropdown Solid - 'Pizza' is Inactive"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    activeField="active"
                    filterable
                    allowClear
                />

                {/* Fill Mode = "Flat" */}
                <BasicDropDownController
                    control={control}
                    name="dropdownFlat"
                    data={data}
                    data-testid="dropdownFlat"
                    label="Dropdown Flat - (Optional)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    fillMode="flat"
                    filterable
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    name="dropdownFlatRequired"
                    required
                    fillMode="flat"
                    data={data}
                    data-testid="dropdownFlatRequired"
                    label="Dropdown Flat - (Required)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    filterable
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    name="dropdownFlatPizzaInvalid"
                    fillMode="flat"
                    data={data}
                    data-testid="dropdownFlatPizzaInvalid"
                    label="Dropdown Flat - ('Pizza' is invalid)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    validateRules={{
                        validatePizza: (value) => (value?.value === 'Pizza' ? 'Pizza is out of stock' : true),
                    }}
                    filterable
                    allowClear
                />

                <BasicDropDown
                    fillMode="flat"
                    data={[]}
                    data-testid="dropdownFlatDisabled"
                    label="Dropdown Flat - (Disabled)"
                    disabled
                    filterable
                    allowClear
                />

                <BasicDropDown
                    fillMode="flat"
                    data={data}
                    textField="value"
                    value={data[0]}
                    data-testid="dropdownFlatReadonly"
                    label="Dropdown Flat - (readonly)"
                    readOnly
                    filterable
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    fillMode="flat"
                    name="dropdownFlatInactive"
                    data={data}
                    value={data[0]}
                    data-testid="dropdownFlatInactiveControlled"
                    label="Dropdown Flat - Inactive"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    activeField="active"
                    filterable
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    fillMode="solid"
                    name="dropdownCustomSuffix"
                    data-testid="dropdownSolidCustomSuffic"
                    data={data}
                    value={data[0]}
                    valid={false}
                    suffix={<Icon dataTestId="dropdownSuffixIcon" icon="filter" title="Suffix Icon" />}
                    textField="value"
                    disabled={isDisabled}
                    label="Dropdown Solid (Custom Suffix)"
                    activeField="active"
                    dataItemKey="id"
                    allowClear
                />

                <Card
                    data-testid="dropdownReadonlyCard"
                    headerActions={
                        <Button data-testid="toggleReadonly" onClick={() => setReadonly(!readonly)}>
                            Toggle Readonly{' '}
                        </Button>
                    }
                >
                    <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                        <BasicDropDown
                            data={data}
                            value={dropdownSolidReadOnly}
                            onChange={(e) => setDropdownSolidReadOnly(e.value as BasicDropDownOption | null)}
                            textField="value"
                            data-testid="dropdownSolidReadonly"
                            label="Dropdown Solid - (readonly)"
                            readOnly={readonly}
                            activeField="active"
                            filterable
                            dataItemKey="id"
                            allowClear
                        />
                        <BasicDropDownController
                            control={control}
                            name="dropdownSolidReadonly"
                            data={data}
                            data-testid="dropdownSolidReadonlyControlled"
                            label="Dropdown Solid - (Readonly Controlled)"
                            disabled={isDisabled}
                            textField="value"
                            dataItemKey="id"
                            readOnly={readonly}
                            filterable
                            allowClear
                        />

                        <BasicDropDown
                            fillMode="flat"
                            data={data}
                            value={dropdownFlatReadOnly}
                            onChange={(e) => setDropdownFlatReadOnly(e.value as BasicDropDownOption | null)}
                            textField="value"
                            data-testid="dropdownFlatReadonly"
                            label="Dropdown Flat - (readonly)"
                            readOnly={readonly}
                            activeField="active"
                            filterable
                            dataItemKey="id"
                            allowClear
                        />

                        <BasicDropDownController
                            control={control}
                            name="dropdownFlatReadonly"
                            data={data}
                            data-testid="dropdownFlatReadonlyControlled"
                            label="Dropdown Flat - (Readonly Controlled)"
                            disabled={isDisabled}
                            textField="value"
                            dataItemKey="id"
                            fillMode="flat"
                            readOnly={readonly}
                            filterable
                            allowClear
                        />
                    </div>
                </Card>

                <BasicDropDownController
                    control={control}
                    name="dropdownSolidFilterable"
                    data={data}
                    data-testid="dropdownSolidFilterable"
                    label="Dropdown Flat - (Optional / Filterable)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    filterable
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    name="dropdownFlatFilterable"
                    data={data}
                    data-testid="dropdownFlatFilterable"
                    label="Dropdown Flat - (Optional / Filterable)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    fillMode="flat"
                    filterable
                    allowClear
                />

                <BasicDropDownController
                    control={control}
                    label="Dropdown Solid - No Data"
                    name="dropdownNoData"
                    data-testid="dropdownNoData"
                    data={[]}
                    textField="value"
                    dataItemKey="id"
                    allowClear
                    disabled={isDisabled}
                    filterable
                />

                <Button type="submit" data-testid="submit-button" primary disabled={isDisabled}>
                    Submit
                </Button>
            </form>
        </div>
    );
};

export default BasicDropDownPage;
